import Slab from "../../components/Slab/Slab";
import FeedList from "../../components/FeedList/FeedList";
import "./FollowingPage.css";
import { useEffect, useState, useRef } from "react";
import { GetFollowings, Unfollow } from "../../services/following.service";
import useAuthService from "../../auth/AuthService";
import { Link, useNavigate } from "react-router-dom";
import { handleDate } from "../../utils/formatdate";
import Button from "../../components/Button/Button";
import NullPageComponent from "../../components/Tile/NullPageComponent";
// import Swal from "sweetalert2";
import { parseMessage } from "../../utils/parseTemplateMessage";

interface FollowingData {
  annualized_value?: Float64Array;
  date_available?: string;
  user_id?: string;
  entity_id?: string;
  id?: string;
  public_id?: string;
  name?: string;
  status?: string;
  user_profile_url?: string;
}

interface FeedData {
  id: string;
  figId: string;
  public_id: string;
  figName: string;
  content: string;
  time: string;
}

export const Following = () => {
  const { getToken, isAuthenticated, getUser } = useAuthService();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [loadingUnfollow, setLoadingUnfollow] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [confirmUnfollowId, setConfirmUnfollowId] = useState<string | null>(null);

  const [state, setState] = useState({
    isLoading: true,
    followings: [] as FollowingData[],
    feeds: [] as FeedData[],
    activeTab: "feed" as "feed" | "following",
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // WebSocket setup
  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const connectWebSocket = async () => {
      const user = getUser();
      if (!user?.sub) return console.error("User not authenticated");

      // Create WebSocket connection
      socket = new WebSocket(
        `${process.env.REACT_APP_FAN1_WEBSOCKET_URL}/api/following/GetFeeds?id=${user.sub}`
      );

      // Handle message from WebSocket
      socket.onmessage = (event) => {
        try {
          const newFeeds: FeedData[] = JSON.parse(event.data);
          setState((prev) => ({
            ...prev,
            feeds: newFeeds,
            isLoading: false,
          }));
        } catch (error) {
          console.error("Error parsing WebSocket data:", error);
        }
      };

      // Handle WebSocket errors
      socket.onerror = () => {
        console.error("WebSocket error. Reconnecting...");
        socket?.close();
        socket = null;
        if (!reconnectTimeout) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      };

      // WebSocket connection close handler
      socket.onclose = () => {
        console.log("WebSocket closed. Reconnecting...");
        if (!reconnectTimeout) {
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      };
    };

    connectWebSocket();

    // Cleanup on component unmount
    return () => {
      socket?.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, [getUser]);

  useEffect(() => {
    const fetchFollowings = async () => {
      if (!isAuthenticated) return;
      try {
        const response: any = await GetFollowings(getToken);
        if (Array.isArray(response.data)) {
          setState((prev) => ({
            ...prev,
            followings: response.data,
          }));
        }
      } catch (error) {
        console.error("Unable to fetch followings.");
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };
    fetchFollowings();
  }, [isAuthenticated, getToken]);

  const handleDropdownToggle = (id: string) => {
    setConfirmUnfollowId(null); 
    setOpenDropdown(openDropdown === id ? null : id); // toggle current dropdown
  };
  
  const handleUnfollow = (entity_id: string) => {
    setOpenDropdown(null); 
    setConfirmUnfollowId(entity_id); 
  };

  const confirmUnfollow = async (entity_id: string) => {
    try {
      setLoadingUnfollow(entity_id);
      await Unfollow(entity_id, getToken);
      setState((prev) => ({
        ...prev,
        followings: prev.followings.filter((f) => f.entity_id !== entity_id),
      }));
    } catch (error) {
      console.error("Failed to unfollow user", error);
    } finally {
      setLoadingUnfollow(null);
      setConfirmUnfollowId(null);
    }
  };
  
  const cancelUnfollow = () => {
    setConfirmUnfollowId(null);
  };
  
  // const handleUnfollow = async (entity_id: string) => {
  //   Swal.fire({
  //     title: "Are you sure?",
  //     showCancelButton: true,
  //     reverseButtons: true,
  //     showConfirmButton: true,
  //     width: 280,
  //     confirmButtonText: "Yes, Unfollow",
  //     cancelButtonText: "Cancel",
  //     // position: 'center',
  //     customClass: {
  //       popup: "Unfollow",
  //       confirmButton: "unfollow-confirm-btn alert-btn",
  //       cancelButton: "unfollow-cancel-btn alert-btn",
  //       title: "title-field",
  //     },
  //     background: "#070115",
  //     color: "#ffffff",
  //     timer: 100000,
  //   }).then(async (result: { isConfirmed: any }) => {
  //     if (result.isConfirmed) {
  //       try {
  //         setLoadingUnfollow(entity_id);
  //         await Unfollow(entity_id, getToken);
  //         setState((prev) => ({
  //           ...prev,
  //           followings: prev.followings.filter(
  //             (f) => f.entity_id !== entity_id
  //           ),
  //         }));
  //       } catch (error) {
  //         console.error("Failed to unfollow user", error);
  //       } finally {
  //         setLoadingUnfollow(null);
  //       }
  //     }
  //   });
  // };

  return (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="container pb-3 following-content sliding-content ps-2">
          {state.followings.length > 0 ? (
            <>
              <h1 className="mt-4 mb-3 d-none d-lg-block">Following</h1>
              <h3 className="my-3 d-block d-lg-none">
                {state.activeTab === "feed" ? "Feed" : "Following"}
              </h3>
              <div className="tab-buttons  mb-3">
                <Button
                  variant="none"
                  label={"Feed"}
                  className={` me-2 ${
                    state.activeTab === "feed"
                      ? "active-pill-buttons"
                      : "pill-buttons"
                  }`}
                  onClick={() =>
                    setState((prev) => ({ ...prev, activeTab: "feed" }))
                  }
                />
                <Button
                  variant="none"
                  label={"Following"}
                  className={` ${
                    state.activeTab === "following"
                      ? "active-pill-buttons"
                      : "pill-buttons"
                  }`}
                  onClick={() =>
                    setState((prev) => ({ ...prev, activeTab: "following" }))
                  }
                />
              </div>
            </>
          ) : (
            <h1 className="following-null-title mb-4">Following</h1>
          )}

          <div className="sliding-row row">
            {state.followings.length === 0 && (
              <div className="px-3">
                <NullPageComponent
                  heading1={"You're not following anyone yet."}
                  heading2={"Search above or see what's hot in Rankings below."}
                  heading3={
                    "Click Follow on any person, place, or thing's individual page to keep tabs on the excitement!"
                  }
                  button1={
                    <>
                      <Button
                        label="Go to Rankings"
                        className="btn"
                        onClick={() => navigate("/rankings")}
                        variant="dark"
                      />
                      <Button
                        label="Go to Available now!"
                        className="btn"
                        onClick={() => navigate("/available")}
                        variant="dark"
                      />
                    </>
                  }
                />
              </div>
            )}
            {/* Feed Section */}
            {state.followings.length > 0 && (
              <div
                className={`col-lg-8 left-slide-panel ${
                  state.activeTab === "feed" ? "active" : ""
                }`}
              >
                <h3 className="my-3 d-none d-lg-block">Feed</h3>
                <div className="box-custom ">
                  <div className="p-3 feed-page-list">
                    {state.feeds &&
                      state.feeds.length > 0 &&
                      state.feeds
                        .map((item, index) => (
                          <FeedList
                            key={item.id}
                            figId={item.figId}
                            figName={item.figName}
                            content={parseMessage(
                              item.content.split("&")[0],
                              navigate
                            )}
                            date={new Date(item.time)}
                            onClick={() =>
                              navigate(`/${item.public_id}`, {
                                state: { id: item.figId },
                              })
                            }
                            className={
                              index === 0
                                ? ""
                                : "mb-3 pb-2 border-bottom border-white"
                            }
                          />
                        ))
                        .reverse()}
                  </div>
                </div>
              </div>
            )}

            {/* Following Section */}
            {state.followings.length > 0 && (
              <div
                className={`col-lg-4 right-slide-panel ${
                  state.activeTab === "following" ? "active" : ""
                }`}
              >
                <h3 className="my-3 d-none d-lg-block">Following</h3>
                <div className="pe-2 m-0 feed-page-list">
                  {state.followings.map((following, index) => (
                    <Slab
                      key={index}
                      className={`${
                        index === state.followings.length - 1 ? "" : "mb-3"
                      } height-70`}
                      content={
                        <>
                          <img
                            src={following.user_profile_url}
                            alt={following.name}
                            className="follow-img col-1 p-0 h-100"
                          />
                          <div className="col d-flex justify-content-between align-items-center">
                            <div className="py-3 text-start content">
                              <Link
                                to={`/${following.public_id}`}
                                state={{ id: following.entity_id }}
                                className="text-decoration-none purple-color font-400 font-18"
                              >
                                {following.name}
                              </Link>
                              <div className="p-0 detail">
                                <div className="w-50">
                                  <i className="fa-solid fa-chart-line me-1"></i>
                                  {Number(following.annualized_value) === 0
                                    ? "---"
                                    : `$${following.annualized_value}`}
                                </div>
                                <div className="w-50">
                                  <i className="fa-regular fa-calendar-days me-1"></i>
                                  {handleDate(
                                    following.date_available?.split(
                                      "T"
                                    )[0] as string
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Three dots icon */}
                            <div
                              className="me-4"
                              style={{ position: "relative" }}
                              ref={openDropdown === following.entity_id ? dropdownRef : null}
                            >
                              <i
                                className="fa-solid fa-ellipsis"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDropdownToggle(following.entity_id!);
                                }}
                              ></i>

                              {openDropdown === following.entity_id && (
                                <button
                                  className="unfollow-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnfollow(following.entity_id!);
                                    
                                  }}
                                  disabled={loadingUnfollow === following.entity_id}
                                >
                                  <span className="font-400-15">Unfollow</span>
                                </button>
                              )}

                              {/* Inline confirmation popup */}
                              {confirmUnfollowId === following.entity_id && (
                                <div className="inline-unfollow-popup">
                                  <div className="font-400-15">Are you sure?</div>
                                  <div className="inline-unfollow-popup-buttons">
                                    <button className="unfollow-cancel-btn font-500-15" onClick={cancelUnfollow}>Cancel</button>
                                    <button
                                      className="unfollow-confirm-btn border border-1 font-500-14 ms-2"
                                      onClick={() => confirmUnfollow(following.entity_id!)}
                                      disabled={loadingUnfollow === following.entity_id}
                                    >
                                      Yes, Unfollow
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      }
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
